import { Injectable, inject } from '@angular/core'
import { JobAddress } from 'src/app/types/dto/job.dto'
import { ChartDataSource, ValueAggregationType } from 'src/app/types/enums'
import { LogService } from './log.service'
import {
  ChartConfig,
  ChartSeries,
  GenericChartItem,
  KNOWN_CHART_ITEM_FIELDS,
} from 'src/app/types/chart.model'
import { DISABLED_SEPARATE_SERIES } from 'src/app/pages/dashboard/edit-card/edit-card.component'
import { DEFAULT_VALUE_FIELD } from 'src/app/types/constants'
import { DatasourceConfigService } from './chart-datasource/datasource-config.service'
import { FilteredItems, SERIES_EMPTY_TEXT } from './chart-datasource/types'

export interface SeriesValue {
  name: string
  value: number
  numItems: number
  percentage: number
}

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  private datasourceConfig = inject(DatasourceConfigService);
  private logger = inject(LogService);


  isMultipleSeries(config: ChartConfig) {
    return !!config.splitByField && config.splitByField !== DISABLED_SEPARATE_SERIES
  }

  showFilteredCategoriesOnly(config: ChartConfig) {
    return (
      config.showFilteredCategoriesOnly ||
      // task-duration charts can't use this yet - always filter
      config.dataSourceId == ChartDataSource.TaskDurations
    )
  }

  static chartValueAggregationIsCount(config: ChartConfig): boolean {
    if (config.trackingField) return false
    return !config.valueField || config.valueField === DEFAULT_VALUE_FIELD
  }

  chartUsingRollingPeriod(config: ChartConfig): boolean {
    if (config.valueAggregationTypeId === undefined) {
      return false
    }
    const aggregationTypeString = ValueAggregationType[config.valueAggregationTypeId]
    return aggregationTypeString?.toLowerCase().includes('rolling') ?? false
  }

  /** @param splitByRankMap optional custom display-order rank (value -> rank) for splitByField's
   *  values, e.g. resolved from a lookup fetched alongside the chart's raw data. If omitted, series
   *  are ordered alphabetically */
  createChartSeries(
    items: FilteredItems,
    config: ChartConfig,
    formatDateFunc: (d: any) => string,
    splitByRankMap?: Map<string, number>
  ): [boolean, boolean, ChartSeries[]] {
    let splittingByDateField = false
    let chartSeries: ChartSeries[] = []

    const isMultipleSeries = this.isMultipleSeries(config)

    if (isMultipleSeries) {
      const uniqueValues = Array.from(
        new Set(
          items.items
            .filter((i) => items.filteredIds.indexOf(i.id) < 0)
            .map((i) => i[config.splitByField])
            .map((i) => {
              if (typeof i === 'boolean') return i.toString()
              else return this.checkForNullOrEmpty(i)
            })
        )
      )
      splittingByDateField = this.datasourceConfig.seriesGroupingIsDate(
        config.dataSourceId,
        config.splitByField
      )
      chartSeries = uniqueValues.map((v) => ({
        label: splittingByDateField ? formatDateFunc(v) : v.toString(),
        value: splittingByDateField ? formatDateFunc(v) : v.toString(),
      }))
    }

    if (splitByRankMap?.size) {
      chartSeries.sort((a, b) => {
        const ra = splitByRankMap.get(a.value.toString())
        const rb = splitByRankMap.get(b.value.toString())
        if (ra != null && rb != null) return ra - rb
        if (ra != null) return -1
        if (rb != null) return 1
        return a.label > b.label ? 1 : -1
      })
    } else {
      chartSeries.sort((a, b) => (a.label > b.label ? 1 : -1))
    }

    return [splittingByDateField, isMultipleSeries, chartSeries]
  }

  getThemePrimaryColour() {
    return window.getComputedStyle(document.documentElement).getPropertyValue('--primary')
  }

  isHttps() {
    return window.location.protocol.startsWith('https') || window.location.hostname === 'localhost'
  }

  arrayMove(arr: any[], fromIndex: number, toIndex: number) {
    const element = arr[fromIndex]
    arr.splice(fromIndex, 1)
    arr.splice(toIndex, 0, element)
  }

  /** accepts any number inc 0 and any string except empty or whitespace strings.
   *  else return SERIES_EMPTY_TEXT
   */
  checkForNullOrEmpty(value: string | number) {
    let isEmpty = false
    const isString = typeof value === 'string'
    if (isString) isEmpty = value.trim().length === 0
    else isEmpty = value === null || value === undefined

    return isEmpty ? SERIES_EMPTY_TEXT : value
  }

  sortByAttribute(attribute: string, isAscending = true, tieBreaker?: (a: any, b: any) => any) {
    const reverseMultiplier = isAscending ? 1 : -1

    return (o: any, p: any) => {
      let result
      const a = o[attribute]
      const b = p[attribute]

      if (a === b) {
        return tieBreaker ? tieBreaker(o, p) : 0
      }

      if (typeof a === typeof b) {
        result = a < b ? -1 : 1
      } else {
        result = typeof a < typeof b ? -1 : 1
      }

      return result * reverseMultiplier
    }
  }

  /** Sorts by the given rank map's value -> rank lookup (ascending, unmatched values ranked
   *  last, falling back to alphabetical on `field` for ties/unranked items). Falls back to a
   *  plain alphabetical sort on `field` if no rank map is supplied. Lets datasources opt in to
   *  a custom display order for a field's values */
  sortByFieldOrCustomRank(field: string, rankMap?: Map<string, number>) {
    if (!rankMap?.size) return this.sortByAttribute(field)

    const alphaTieBreaker = this.sortByAttribute(field)
    return (o: any, p: any) => {
      const a = rankMap.get(o[field]) ?? rankMap.size
      const b = rankMap.get(p[field]) ?? rankMap.size
      if (a !== b) return a - b
      return alphaTieBreaker(o, p)
    }
  }

  lowerCaseFirstLetter(str: string): string {
    return str.charAt(0).toLocaleLowerCase() + str.slice(1)
  }

  capitaliseWords(str: string): string {
    return str.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
  }

  groupBy(
    input: any[],
    key: string,
    keyFunc?: (item: any) => any,
    replaceEmptyWith?: string
  ): any[][] {
    return input.reduce((previous, currentItem) => {
      let groupKey = currentItem[key]

      // Replace null, undefined, or empty string with the replaceEmptyWith value
      if (!groupKey && replaceEmptyWith) {
        groupKey = replaceEmptyWith
      } else if (!groupKey && !replaceEmptyWith) {
        return previous
      }

      // Apply keyFunc if provided
      if (keyFunc) {
        groupKey = keyFunc(groupKey)
      }

      if (!previous[groupKey]) {
        previous[groupKey] = []
      }

      previous[groupKey].push(currentItem)

      return previous
    }, [])
  }

  round(num: number, places: number): number {
    if (places < 0) {
      this.logger.log('utils', 'round', null, 'negative num')
      throw Error('negative number in round()')
    }
    const modifier = Math.pow(10, places)
    return Math.round((num + Number.EPSILON) * modifier) / modifier
  }

  getDefaultValueAggregationPrefix(type: ValueAggregationType): string {
    switch (type) {
      case ValueAggregationType.Average:
        return 'Average '
      case ValueAggregationType.Sum:
        return 'Sum of '
      case ValueAggregationType.Cumulative:
        return 'Cumulative '
      case ValueAggregationType['Average - Rolling 3 Period']:
        return '3 Period Average '
      case ValueAggregationType['Average - Rolling 6 Period']:
        return '6 Period Average '
    }
  }

  generateJobAddressString(address: JobAddress): string {
    if (!address) {
      return ''
    }

    let string = ''

    if (address.lotNumber && address.lotNumber.trim() !== '') {
      string = string.concat('Lot ' + address.lotNumber.trim())
    }
    if (address.streetNumber && address.streetNumber.trim() !== '') {
      string = string.concat(' #' + address.streetNumber.trim())
    }
    if (address.streetName1 && address.streetName1.trim() !== '') {
      string = string.concat(' ' + address.streetName1.trim())
    }
    if (address.streetName2 && address.streetName2.trim() !== '') {
      string = string.concat(' ' + address.streetName2.trim())
    }
    if (address.suburbTown && address.suburbTown.trim() !== '') {
      string = string.concat(' ' + address.suburbTown.trim())
    }
    if (address.postCode && address.postCode.trim() !== '') {
      string = string.concat(' ' + address.postCode.trim())
    }

    return string
  }

  getDynamicSeriesFromChartItem(
    item: GenericChartItem,
    removeSeriesWithNoItems = false
  ): SeriesValue[] {
    const fields = Object.keys(item)
    const filtered = fields.filter((s) => !KNOWN_CHART_ITEM_FIELDS.includes(s))

    let seriesWithValues = filtered.map((s) => ({
      name: s,
      value: item[s] ?? 0,
      numItems: item.seriesNumItems[s] ?? 0,
    }))

    if (removeSeriesWithNoItems)
      seriesWithValues = seriesWithValues.filter((s) => item.seriesNumItems[s.name])

    const total = seriesWithValues.reduce((sum, s) => sum + s.value, 0)

    let series: SeriesValue[] = seriesWithValues.map((s) => {
      return {
        ...s,
        percentage: total > 0 ? this.round((s.value / total) * 100, 2) : 0,
      }
    })

    series = this.orderBy(series, ['value', 'numItems'], ['desc', 'desc'])
    return series
  }

  /** orderBy(users, ["name", "age"], ["asc", "desc"]),
      orderBy(users, ["name", "age"]); */
  orderBy<T>(arr: T[], props: (keyof T)[], orders?: ('asc' | 'desc')[]) {
    return [...arr].sort((a, b) =>
      props.reduce((acc, prop, i) => {
        if (acc === 0) {
          const [p1, p2] = orders && orders[i] === 'desc' ? [b[prop], a[prop]] : [a[prop], b[prop]]
          acc = p1 > p2 ? 1 : p1 < p2 ? -1 : 0
        }
        return acc
      }, 0)
    )
  }

  // Helper to recursively patch dx filter so that "blank" includes empty strings, undefined as well as null
  static patchIsBlankFilter(filter: any): any {
    if (!filter) return filter

    if (Array.isArray(filter)) {
      if (
        filter.length === 3 &&
        typeof filter[0] === 'string' &&
        (filter[1] === '=' || filter[1] === '==') &&
        (filter[2] === null || filter[2] === undefined)
      ) {
        return [
          [filter[0], filter[1], null],
          'or',
          [filter[0], filter[1], undefined],
          'or',
          [filter[0], filter[1], ''],
        ]
      }

      if (
        filter.length === 3 &&
        typeof filter[0] === 'string' &&
        (filter[1] === '!=' || filter[1] === '<>') &&
        (filter[2] === null || filter[2] === undefined)
      ) {
        return [
          [filter[0], filter[1], null],
          'and',
          [filter[0], filter[1], undefined],
          'and',
          [filter[0], filter[1], ''],
        ]
      }

      // Otherwise, recursively patch each element
      return filter.map(UtilsService.patchIsBlankFilter)
    }

    // Not an array, return as is
    return filter
  }

  chartUsingAverageValueAggregation(config: ChartConfig): boolean {
    return (
      config.valueAggregationTypeId === ValueAggregationType.Average ||
      config.valueAggregationTypeId === ValueAggregationType['Average - Rolling 3 Period'] ||
      config.valueAggregationTypeId === ValueAggregationType['Average - Rolling 6 Period']
    )
  }
}
